import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useCreateGoal, useGoals, useMe, useOrgMe, usePeople, useUpdateGoal, type Goal, type Person } from "../api/hooks";
import { Button, Chip, Empty, ErrorBox, Field, Loading, Page, Table, Tabs, statusTone, type Tone } from "../components/ui";
import { fmtDate, title } from "../lib/format";

export const Route = createFileRoute("/goals")({ component: Goals });

/** Everyone this login may see by name: the whole organisation for an HR admin, the
 *  reporting line for a manager. A key with no match stays a key rather than vanishing. */
function useNames() {
  const people = usePeople();
  const org = useOrgMe();
  const all: Person[] = [...(people.data ?? []), ...(org.data?.direct_reports ?? []), ...(org.data?.me ? [org.data.me] : [])];
  const nameOf = (key: string | null | undefined) => (key ? (all.find((p) => p.person_key === key)?.full_name ?? key) : "—");
  return { all, nameOf };
}

function scopeLabel(g: Goal, nameOf: (k: string) => string) {
  switch (g.scope_type) {
    case "company": return "Company";
    case "person": return `Person · ${nameOf(g.scope_key)}`;
    default: return `${title(g.scope_type)} · ${g.scope_key}`;
  }
}

function targetLabel(g: Goal) {
  const value = g.target_value != null ? `${g.target_value}${g.unit ? ` ${g.unit}` : ""}` : null;
  const date = g.target_date ? fmtDate(g.target_date) : null;
  return [value, date].filter(Boolean).join(" · ") || "—";
}

const LIVE = new Set(["on_track", "at_risk", "off_track"]);
type View = "all" | "at_risk" | "overdue" | "completed";

/** Overdue: a live goal whose target date has passed. */
function isOverdue(g: Goal, today: string) {
  return LIVE.has(g.status) && !!g.target_date && g.target_date < today;
}

function Goals() {
  const goals = useGoals();
  const update = useUpdateGoal();
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  const { all, nameOf } = useNames();
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<View>("all");
  const [today] = useState(() => new Date().toISOString().slice(0, 10));
  const list = useMemo(() => goals.data ?? [], [goals.data]);
  const live = list.filter((g) => LIVE.has(g.status));
  const scored = list.filter((g) => LIVE.has(g.status) || g.status === "completed");
  const counts = {
    completed: list.filter((g) => g.status === "completed").length,
    on_track: list.filter((g) => g.status === "on_track").length,
    at_risk: list.filter((g) => g.status === "at_risk" || g.status === "off_track").length,
    overdue: list.filter((g) => isOverdue(g, today)).length,
  };
  const pct = (n: number, d: number) => (d > 0 ? Math.round((100 * n) / d) : null);
  const completion = pct(counts.completed + counts.on_track, scored.length);
  const avgProgress = live.length ? Math.round(live.reduce((a, g) => a + g.progress_pct, 0) / live.length) : null;
  // Progress by department: department goals by their scope, everything else by its owner's department.
  const byDept = useMemo(() => {
    const dept = (g: Goal) => (g.scope_type === "department" ? g.scope_key : g.scope_type === "company" ? "Company" : (all.find((p) => p.person_key === g.owner_person_key)?.department ?? "Unassigned"));
    const m = new Map<string, Goal[]>();
    for (const g of list) m.set(dept(g), [...(m.get(dept(g)) ?? []), g]);
    return [...m.entries()].map(([d, gs]) => ({ dept: d, n: gs.length, on_track: gs.filter((g) => g.status === "on_track").length, at_risk: gs.filter((g) => g.status === "at_risk").length, off_track: gs.filter((g) => g.status === "off_track").length, completed: gs.filter((g) => g.status === "completed").length, draft: gs.filter((g) => g.status === "draft").length })).sort((a, b) => b.n - a.n);
  }, [list, all]);
  const shown = view === "all" ? list : view === "at_risk" ? list.filter((g) => g.status === "at_risk" || g.status === "off_track") : view === "overdue" ? list.filter((g) => isOverdue(g, today)) : list.filter((g) => g.status === "completed");
  return (
    <Page title="Goals and execution" sub="Company, department, team and personal goals: progress, what is at risk, and where execution is drifting." actions={<Button variant="light" onClick={() => setCreating((v) => !v)}>{creating ? "Cancel" : "+ New goal"}</Button>}>
      {error != null && <div className="mb-3"><ErrorBox error={error} /></div>}
      {creating && <NewGoal people={all} onDone={() => setCreating(false)} onError={setError} />}
      {goals.data && goals.data.length > 0 && (
        <>
          <div className="grid grid-cols-5 gap-3 mb-4">
            <Tile label="Overall goal completion" value={completion} suffix="%" tone={completion == null ? "neutral" : completion >= 70 ? "good" : completion >= 50 ? "elevated" : "high"} note="on track or completed" />
            <Tile label="Goals on track" value={pct(counts.on_track, live.length)} suffix="%" tone="neutral" note={`${counts.on_track} of ${live.length} live goals`} />
            <Tile label="At risk goals" value={pct(counts.at_risk, live.length)} suffix="%" tone={counts.at_risk === 0 ? "good" : (pct(counts.at_risk, live.length) ?? 0) >= 25 ? "high" : "elevated"} note={`${counts.at_risk} at risk or off track`} />
            <Tile label="Overdue goals" value={pct(counts.overdue, live.length)} suffix="%" tone={counts.overdue === 0 ? "good" : "high"} note={`${counts.overdue} past their target date`} />
            <Tile label="Average progress" value={avgProgress} suffix="%" tone="neutral" note="across live goals" />
          </div>
          {isAdmin && byDept.length > 0 && (
            <div className="card mb-4">
              <div className="flex items-baseline justify-between">
                <h4 className="font-bold text-[13.5px]">Goal progress by department</h4>
                <div className="flex gap-3 text-[11.5px] text-muted">
                  {([["on_track", "On track", "var(--status-good)"], ["at_risk", "At risk", "var(--status-elevated)"], ["off_track", "Off track", "var(--status-high)"], ["completed", "Completed", "var(--blue)"], ["draft", "Draft", "var(--line-2)"]] as const).map(([k, l, c]) => (
                    <span key={k} className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: c }} aria-hidden />{l}</span>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2">
                {byDept.map((d) => (
                  <div key={d.dept} className="flex items-center gap-3 text-[12.5px]">
                    <span className="w-[150px] truncate font-semibold">{d.dept}</span>
                    <div className="flex-1 h-[8px] rounded-full overflow-hidden flex bg-[var(--line)]" aria-hidden>
                      {([["on_track", "var(--status-good)"], ["at_risk", "var(--status-elevated)"], ["off_track", "var(--status-high)"], ["completed", "var(--blue)"], ["draft", "var(--line-2)"]] as const).map(([k, c]) => d[k] > 0 && <div key={k} style={{ width: `${(100 * d[k]) / d.n}%`, background: c }} />)}
                    </div>
                    <span className="w-6 text-right text-muted">{d.n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Tabs
            tabs={[
              { key: "all", label: "All goals", count: list.length },
              { key: "at_risk", label: "At risk", count: counts.at_risk },
              { key: "overdue", label: "Overdue", count: counts.overdue },
              { key: "completed", label: "Completed", count: counts.completed },
            ]}
            active={view}
            onChange={setView}
          />
        </>
      )}
      {goals.isPending ? (
        <Loading />
      ) : goals.isError ? (
        <ErrorBox error={goals.error} />
      ) : list.length === 0 ? (
        <Empty title="No goals yet" />
      ) : shown.length === 0 ? (
        <Empty title={view === "overdue" ? "Nothing overdue" : view === "at_risk" ? "Nothing at risk" : "Nothing completed yet"} />
      ) : (
        <Table
          rows={shown}
          keyOf={(g) => g.id}
          cols={[
            { h: "Goal", cell: (g: Goal) => <><span className="font-semibold">{g.title}</span>{g.description && <span className="sub">{g.description}</span>}</> },
            { h: "Scope", cell: (g) => <span className="text-muted">{scopeLabel(g, nameOf)}</span>, w: "180px" },
            { h: "Owner", cell: (g) => <span className="text-muted">{nameOf(g.owner_person_key)}</span>, w: "140px" },
            { h: "Target", cell: (g) => <span className={isOverdue(g, today) ? "font-semibold" : "text-muted"} style={isOverdue(g, today) ? { color: "var(--status-high)" } : undefined}>{targetLabel(g)}{isOverdue(g, today) ? " · overdue" : ""}</span>, w: "170px" },
            { h: "Progress", cell: (g) => <Progress pct={g.progress_pct} />, w: "160px" },
            { h: "Status", cell: (g) => <Chip tone={statusTone(g.status)}>{title(g.status)}</Chip>, w: "120px" },
            {
              h: "",
              w: "190px",
              cell: (g) => (
                <div className="flex gap-1.5 justify-end">
                  {["on_track", "at_risk", "off_track"].filter((s) => s !== g.status && !["completed", "cancelled"].includes(g.status)).map((s) => (
                    <Button key={s} variant="light" className="!py-1 !px-2.5 !text-[12px]" disabled={update.isPending} onClick={() => update.mutate({ id: g.id, status: s, progress_pct: g.progress_pct, current_value: g.current_value ?? null }, { onError: setError })}>{title(s)}</Button>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}
    </Page>
  );
}

function Tile({ label, value, suffix, tone, note }: { label: string; value: number | null; suffix: string; tone: Tone; note: string }) {
  return (
    <div className="card kpi">
      <h4>{label}</h4>
      <div className="v">
        <span>{value != null ? `${value}${suffix}` : "—"}</span>
        {value != null && tone !== "neutral" && <Chip tone={tone}>{tone === "good" ? "On track" : tone === "elevated" ? "Watch" : "Needs attention"}</Chip>}
      </div>
      <div className="delta">{note}</div>
    </div>
  );
}

function Progress({ pct }: { pct: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-tile overflow-hidden"><div className="h-full bg-blue" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} /></div>
      <span className="text-[12px] text-muted w-9 text-right">{pct}%</span>
    </div>
  );
}

function PersonSelect({ value, onChange, required, people }: { value: string; onChange: (v: string) => void; required?: boolean; people: Person[] }) {
  const sorted = [...people].sort((a, b) => (a.full_name ?? a.person_key).localeCompare(b.full_name ?? b.person_key));
  return (
    <select className="input" required={required} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{required ? "Choose…" : "Nobody"}</option>
      {sorted.map((p) => <option key={p.person_key} value={p.person_key}>{p.full_name ?? p.person_key}</option>)}
    </select>
  );
}

function NewGoal({ people, onDone, onError }: { people: Person[]; onDone: () => void; onError: (e: unknown) => void }) {
  const create = useCreateGoal();
  const [f, setF] = useState({ title: "", description: "", scope_type: "team", scope_key: "", owner_person_key: "", target_value: "", unit: "", target_date: "" });
  return (
    <form
      className="card grid grid-cols-4 gap-3 mb-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          { title: f.title, description: f.description || null, scope_type: f.scope_type, scope_key: f.scope_type === "company" ? "company" : f.scope_key, owner_person_key: f.owner_person_key || null, target_value: f.target_value ? Number(f.target_value) : null, unit: f.unit || null, target_date: f.target_date || null },
          { onSuccess: onDone, onError },
        );
      }}
    >
      <Field label="Title"><input className="input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
      <Field label="Description"><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <Field label="Scope type"><select className="input" value={f.scope_type} onChange={(e) => setF({ ...f, scope_type: e.target.value })}>{["company", "department", "team", "person"].map((t) => <option key={t} value={t}>{title(t)}</option>)}</select></Field>
      {f.scope_type === "person" ? (
        <Field label="Person"><PersonSelect required people={people} value={f.scope_key} onChange={(v) => setF({ ...f, scope_key: v })} /></Field>
      ) : f.scope_type === "company" ? (
        <Field label="Scope"><input className="input" disabled value="Whole company" /></Field>
      ) : (
        <Field label={f.scope_type === "team" ? "Team key" : "Department"}><input className="input" required value={f.scope_key} onChange={(e) => setF({ ...f, scope_key: e.target.value })} /></Field>
      )}
      <Field label="Owner"><PersonSelect people={people} value={f.owner_person_key} onChange={(v) => setF({ ...f, owner_person_key: v })} /></Field>
      <Field label="Target value"><input className="input" type="number" step="any" value={f.target_value} onChange={(e) => setF({ ...f, target_value: e.target.value })} /></Field>
      <Field label="Unit"><input className="input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></Field>
      <Field label="Target date"><input className="input" type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} /></Field>
      <div className="col-span-4 flex gap-2"><Button type="submit" disabled={create.isPending}>Create</Button><Button type="button" variant="light" onClick={onDone}>Cancel</Button></div>
    </form>
  );
}
